// VerifyEmailComponent.js
import React, { useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import {
  emailAtom,
  registeredUserIdAtom,
  submitAccessibilityAtom,
} from "@/store/atoms";
import Loader from "@/components/common/Loader";
import {
  useAccessibility,
  useI18N,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";
import { createPageAnalytics, setUserProperties } from "common/analytics";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import WideButton from "@/components/common/WideButton";

interface VerifyEmailProps {
  title: string;
  subtitle: string;
}

const VerifyEmailComponent = ({ title, subtitle }: VerifyEmailProps) => {
  const isZoomedIn = useZoomDetection();
  const i18n = useI18N();
  const [email] = useAtom(emailAtom);
  const { setScreenIndex } = useAuthModals();
  const { modalStyle, modalClassName } = useModalHeight();
  const [registeredUserId] = useAtom(registeredUserIdAtom);

  const {
    submitAccessibility,
    submitAccessibilityError,
    submitAccessibilityResponse,
    submitAccessibilityStatus,
    filterAccessibility,
    howDoYouHearAboutOptions,
  } = useAccessibility();

  //get and store the data for entire onBoarding steps
  const [submitData] = useAtom(submitAccessibilityAtom);

  const isLoading = submitAccessibilityStatus === "pending";
  const verifyEmailAnalytics = createPageAnalytics("verify_email");

  useEffect(() => {
    // verify_email_viewed (stub payload). Note: web has no "Open email app" button,
    // so email_app_cta_activated is RN-only and not instrumented here.
    verifyEmailAnalytics.track("page_view_page", {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (submitData && registeredUserId) {
      const dataToSubmit = {
        ...submitData,
        userId: registeredUserId,
      };
      submitAccessibility(dataToSubmit);
    }
  }, [submitData, registeredUserId, submitAccessibility]);

  useEffect(() => {
    if (submitAccessibilityError) {
      console.log(
        "submitAccessibilityError",
        submitAccessibilityError?.message,
      );
    }
  }, [submitAccessibilityError]);

  useEffect(() => {
    if (submitAccessibilityResponse) {
      console.log(
        "submitAccessibilityResponse",
        submitAccessibilityResponse?.message,
      );
    }
  }, [email, submitAccessibilityResponse]);

  // Centralized user (people) properties captured from the profile-setup wizard,
  // set once the profile update has persisted. account_creation_date/method are
  // set earlier at identify time (the account-creation moment).
  useEffect(() => {
    if (!submitAccessibilityResponse || !submitData) return;

    // saved_accessibility_filter: wizard stores tag ids -> map to tag names.
    const tagNameById = new Map<string, string>();
    filterAccessibility.forEach((group) => {
      group?.accessibility_tags?.forEach((tag) => {
        tagNameById.set(String(tag.id), tag.name);
      });
    });
    const savedAccessibilityFilter = (
      submitData.accessibilityFeatureRequestTagsValue || ""
    )
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean)
      .map((id) => tagNameById.get(id) ?? id);

    // referral_source: wizard stores "how do you hear" option ids -> map to titles.
    const referralNameById = new Map<string, string>();
    howDoYouHearAboutOptions.forEach((opt) => {
      referralNameById.set(String(opt.id), opt.title);
    });
    const referralSource = (submitData.how_do_you_hear_about_us_ids || "")
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean)
      .map((id) => referralNameById.get(id) ?? id)
      .join(", ");

    // accessibility_profile: comma-separated accessibility-need display names.
    const accessibilityProfile = (submitData.ability || "")
      .split(",")
      .map((name) => name.trim())
      .filter(Boolean);

    setUserProperties({
      accessibility_profile: accessibilityProfile,
      profile_visibility: submitData.makeDisabilityVisibleValue
        ? "public"
        : "private",
      saved_accessibility_filter: savedAccessibilityFilter,
      ...(referralSource ? { referral_source: referralSource } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitAccessibilityResponse]);

  const onContinue = async () => {
    //move to Login
    setScreenIndex(AuthModalScreen.EnterEmail);
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  console.log("submitData from OnboardingStep5", submitData);

  return (
    <div
      style={modalStyle}
      className="flex flex-col rounded-xl bg-bg-brand-secondary-default overflow-y-auto"
      role="none"
      aria-labelledby="modal-title"
    >
      <header
        className={`py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
      >
        <h1
          className="title-s-semibold text-center text-fg-neutral-strong"
          ref={firstFocusRef}
          tabIndex={0}
          id="modal-title"
        >
          {i18n.get("verifyEmailAddress")}
        </h1>
      </header>
      <div className="flex-1 px-8 md:px-16 overflow-y-auto">
        <div className="mt-6">
          <h2 className="mb-4 text-fg-neutral-strong title-m-bold">{title}</h2>
          <p className="title-s-semibold text-fg-neutral-strong">{subtitle}</p>
        </div>
        <div>
          <hr className="w-full border-border-token-brand-minimal my-6" />
        </div>
        <div aria-label="Model Content">
          {/* content comes here */}
          <div>
            <p className="title-s-semibold text-fg-neutral-strong mb-3">
              {i18n.get("wehaveJustSentAnEmailTo")}
            </p>
            <p className="mb-6 title-s-semibold !text-fg-brand-subtle">
              {email}
            </p>
            <p className="label-m-medium text-fg-neutral-strong">
              {i18n.get("clickTheLinkInsideToLogInToSociability")}
            </p>
          </div>
          {/* content comes here */}
        </div>
        {isLoading && <Loader />}
        <div className="mb-16 mt-8">
          <WideButton onClick={onContinue}>
            {i18n.get("returnToLoginPage")}
          </WideButton>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailComponent;
